import { ReactNode, useState } from 'react';

import { ClientJoin } from '../../components/join-meet/client';
import { PsychologJoin } from '../../components/join-meet/psycholog';
import { GroupClientLobby } from '../../components/meet/group-client-lobby';
import { getConsultationClientName, getGroupDisplayName } from '../../features/meet/meet.adapters';
import { useMeetModeration } from '../../features/meet/MeetModerationProvider';
import { useMeetOverlays } from '../../features/meet/MeetOverlaysProvider';
import { IClient, IConsultation } from '../../store/types';

interface MeetJoinFlowProps {
  consultation: IConsultation;
  client?: IClient;
  users: unknown[];
  isPsychologist: boolean;
  isGroupConsultationFlag: boolean;
  videoRoomInitialized: boolean;
  settingsModal: ReactNode;
  startWithMedia: (value: boolean, isCameraEnabled?: boolean) => void | Promise<void>;
  onOpenSettingsChange: (value: boolean) => void | Promise<void>;
}

export function MeetJoinFlow({
  consultation,
  client,
  users,
  isPsychologist,
  isGroupConsultationFlag,
  videoRoomInitialized,
  settingsModal,
  startWithMedia,
  onOpenSettingsChange,
}: MeetJoinFlowProps) {
  const overlays = useMeetOverlays();
  const moderation = useMeetModeration();
  const [preferredCameraEnabled, setPreferredCameraEnabled] = useState(true);
  const consultationClientName = getConsultationClientName(consultation);
  const groupDisplayName = isGroupConsultationFlag
    ? getGroupDisplayName(client?.full_name, consultationClientName)
    : undefined;

  if (isGroupConsultationFlag && !isPsychologist && moderation.groupPermissionRequested) {
    return (
      <GroupClientLobby
        connectionAllowed={moderation.connectionAllowedForGroupClient}
        initialCameraEnabled={preferredCameraEnabled}
        onEnterMeeting={(isCameraEnabled) => startWithMedia(true, isCameraEnabled)}
      />
    );
  }

  return (
    <div className="w-full h-full">
      {isPsychologist ? (
        <PsychologJoin
          setOpenSettings={onOpenSettingsChange}
          setStarted={startWithMedia}
          consultation={consultation}
          client={client}
          users={users}
          disabled={!videoRoomInitialized}
          isRoomInitialized={videoRoomInitialized}
          isGroupConsultation={isGroupConsultationFlag}
        />
      ) : (
        <ClientJoin
          consultation={consultation}
          groupName={groupDisplayName}
          setStarted={startWithMedia}
          onRequestPermission={
            isGroupConsultationFlag
              ? (name, isCameraEnabled) => {
                  setPreferredCameraEnabled(isCameraEnabled);
                  moderation.requestGroupPermission(name);
                }
              : undefined
          }
          setOpenSettings={onOpenSettingsChange}
          users={users}
          disabled={!videoRoomInitialized}
          isRoomInitialized={videoRoomInitialized}
          isGroupConsultation={isGroupConsultationFlag}
        />
      )}
      {overlays.openSettings && settingsModal}
    </div>
  );
}
